<script setup lang="ts">
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
  CalendarRoot,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
} from 'reka-ui';
import type { DateValue } from 'reka-ui';
import { CLIENT_EVENTS_URL } from '~/shared/api/client/events/events.const';
import { getMonthName } from '~/shared/utils/formatDate';
import type { EventDateInfo } from '../lib/const';
import { useEventCalendar } from '../lib/useEventCalendar';
import CalendarDayCell from './CalendarDayCell.vue';
import CalendarNavGroup from './CalendarNavGroup.vue';

const $style = useCssModule();
const router = useRouter();
const isOpen = ref(false);

const {
  placeholder,
  currentMonth,
  currentYear,
  prevMonth,
  nextMonth,
  prevYear,
  nextYear,
  getEventInfo,
} = useEventCalendar();

function getCellClasses(info: EventDateInfo | null) {
  return [
    $style.cell,
    info?.type === 'cre' && $style.cell_cre,
    info?.type === 'partner' && $style.cell_partner,
    info?.position === 'start' && $style.cell_start,
    info?.position === 'middle' && $style.cell_middle,
    info?.position === 'end' && $style.cell_end,
  ];
}

function handleDateClick(day: DateValue) {
  const info = getEventInfo(day);
  if (!info) return;
  isOpen.value = false;
  router.push(`${CLIENT_EVENTS_URL}${info.id}`);
}
</script>

<template>
  <PopoverRoot v-model:open="isOpen">
    <PopoverTrigger as-child>
      <Button theme="white" size="small" :class="$style.calendarBtn">
        <span :class="$style.calendarBtn__content">
          <Icon name="ic:outline-calendar-month" size="18" />
          <span :class="$style.calendarBtn__label">Календарь мероприятий</span>
        </span>
      </Button>
    </PopoverTrigger>

    <PopoverPortal>
      <PopoverContent
        :class="$style.content"
        :side-offset="0"
        side="bottom"
        align="end"
      >
        <CalendarRoot
          v-slot="{ weekDays, grid }"
          v-model:placeholder="placeholder"
          :class="$style.calendar"
          locale="ru"
          weekday-format="short"
        >
          <div :class="$style.header">
            <CalendarNavGroup
              :label="getMonthName(currentMonth - 1, 'nominative')"
              @prev="prevMonth"
              @next="nextMonth"
            />
            <CalendarNavGroup
              :label="String(currentYear)"
              @prev="prevYear"
              @next="nextYear"
            />
          </div>

          <div :class="$style.grid__wrapper">
            <CalendarGrid
              v-for="month in grid"
              :key="month.value.toString()"
              :class="$style.grid"
            >
              <CalendarGridHead>
                <CalendarGridRow :class="$style.grid__row">
                  <CalendarHeadCell
                    v-for="day in weekDays"
                    :key="day"
                    :class="$style.grid__headCell"
                  >
                    {{ day }}
                  </CalendarHeadCell>
                </CalendarGridRow>
              </CalendarGridHead>

              <CalendarGridBody>
                <CalendarGridRow
                  v-for="(weekDates, index) in month.rows"
                  :key="`week-${index}`"
                  :class="$style.grid__row"
                >
                  <CalendarDayCell
                    v-for="weekDate in weekDates"
                    :key="weekDate.toString()"
                    :day="weekDate"
                    :month="month.value"
                    :get-event-info="getEventInfo"
                    :get-cell-classes="getCellClasses"
                    @date-click="handleDateClick"
                  />
                </CalendarGridRow>
              </CalendarGridBody>
            </CalendarGrid>
          </div>
        </CalendarRoot>

        <div :class="$style.legend">
          <div :class="$style.legend__item">
            <span :class="[$style.legend__dot, $style.legend__dot_cre]" />
            <span :class="$style.legend__text">Мероприятия CRE</span>
          </div>
          <div :class="$style.legend__item">
            <span :class="[$style.legend__dot, $style.legend__dot_partner]" />
            <span :class="$style.legend__text">Партнерские мероприятия</span>
          </div>
        </div>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<style module lang="scss">
$color-partner: #4982ff;
$color-cre-multi-bg: #38191c;
$color-partner-multi-bg: #141e33;

.calendarBtn {
  height: 40px;
  margin-left: auto;
  white-space: nowrap;

  &[data-state='open'] {
    background-color: var(--color-black-1);
    color: var(--color-white);
  }
}

.calendarBtn__content {
  display: flex;
  align-items: center;
  gap: var(--space-1x);
}

.calendarBtn__label {
  @include text_button3;
}

.content {
  background-color: var(--color-black-1);
  width: 393px;
  padding: 14px 11px 20px;
  z-index: 100;
  transform-origin: var(--reka-popover-content-transform-origin);
  animation: fadeIn 150ms ease-out;
}

.calendar {
  width: 100%;

  // Hide the built-in accessible heading rendered by CalendarRoot
  :global([data-reka-calendar-heading]) {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
}

.grid__wrapper {
  width: 100%;
}

.grid {
  width: 100%;
  user-select: none;
  border-collapse: collapse;
}

.grid__row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  width: 100%;
}

.grid__headCell {
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-golos);
  font-size: 12px;
  line-height: 1.2;
  color: var(--color-black-2);
  text-align: center;
}

.cell {
  position: relative;
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cell_cre {
  border: 1px solid var(--color-red-2);

  &.cell_middle {
    border: none;
    background-color: $color-cre-multi-bg;
  }
}

.cell_partner {
  border: 1px solid $color-partner;

  &.cell_middle {
    border: none;
    background-color: $color-partner-multi-bg;
  }
}

.legend {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 22px 0;
}

.legend__item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.legend__dot {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.legend__dot_cre {
  background-color: var(--color-red-2);
}

.legend__dot_partner {
  background-color: $color-partner;
}

.legend__text {
  font-family: var(--font-golos);
  font-weight: 500;
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: var(--color-white);
  white-space: nowrap;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 599px) {
  .calendarBtn__label {
    display: none;
  }

  .calendarBtn {
    width: auto;
    padding: 7px 12px;
  }

  .content {
    width: calc(100vw - 20px);
  }
}
</style>
